export interface ApiClientOptions {
  baseUrl?: string;
  fetchImplementation?: typeof fetch;
  csrfTokenProvider?: () => string | null;
}

export interface ApiProblem {
  readonly code?: string;
  readonly correlationId?: string;
  readonly detail?: string;
  readonly status?: number;
  readonly title?: string;
  readonly type?: string;
}

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly problem: ApiProblem | null,
  ) {
    super(problem?.title ?? `API request failed with status ${String(status)}.`);
    this.name = "ApiRequestError";
  }
}

export interface MutationOptions {
  readonly idempotencyKey?: string;
  readonly ifMatch?: number;
  readonly signal?: AbortSignal;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImplementation: typeof fetch;
  private readonly csrfTokenProvider: () => string | null;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? "/api";
    this.fetchImplementation = options.fetchImplementation ?? globalThis.fetch.bind(globalThis);
    this.csrfTokenProvider = options.csrfTokenProvider ?? (() => null);
  }

  async get<T>(path: string, signal?: AbortSignal): Promise<T> {
    const request = this.fetchImplementation;
    const response = await request(`${this.baseUrl}${path}`, {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      ...(signal ? { signal } : {}),
    });

    await this.assertOk(response);

    return (await response.json()) as T;
  }

  async post<T>(path: string, body: unknown, options: MutationOptions = {}): Promise<T> {
    const response = await this.mutate("POST", path, body, options);
    return (await response.json()) as T;
  }

  /**
   * Raw octet-stream POST for M18 uploads: same origin/CSRF discipline as
   * every other mutation, but the body is untouched bytes and the caller
   * supplies the provenance x-headers the document registry requires.
   */
  async postBinary<T>(
    path: string,
    body: ArrayBuffer | Uint8Array,
    headers: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T> {
    const csrfToken = this.csrfTokenProvider();
    if (csrfToken === null || csrfToken.length === 0) {
      throw new Error("CSRF_TOKEN_UNAVAILABLE");
    }
    const response = await this.fetchImplementation(`${this.baseUrl}${path}`, {
      body: body as BodyInit,
      cache: "no-store",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/octet-stream",
        "x-csrf-token": csrfToken,
        ...headers,
      },
      method: "POST",
      ...(signal ? { signal } : {}),
    });
    await this.assertOk(response);
    return (await response.json()) as T;
  }

  async put<T>(path: string, body: unknown, options: MutationOptions = {}): Promise<T> {
    return this.mutateJson<T>("PUT", path, body, options);
  }

  async patch<T>(path: string, body: unknown, options: MutationOptions = {}): Promise<T> {
    return this.mutateJson<T>("PATCH", path, body, options);
  }

  async delete<T>(path: string, body: unknown = undefined, options: MutationOptions = {}): Promise<T> {
    return this.mutateJson<T>("DELETE", path, body, options);
  }

  /**
   * Shared unsafe-request path. `post` uses it directly so its behaviour is
   * unchanged; `put`, `patch` and `delete` add only the empty-body tolerance the
   * API's declared `204 No Content` responses require.
   */
  private async mutate(
    method: "POST" | "PUT" | "PATCH" | "DELETE",
    path: string,
    body: unknown,
    options: MutationOptions,
  ): Promise<Response> {
    const csrfToken = this.csrfTokenProvider();
    if (csrfToken === null || csrfToken.length === 0) {
      throw new Error("CSRF_TOKEN_UNAVAILABLE");
    }
    const headers: Record<string, string> = {
      Accept: "application/json",
      "x-csrf-token": csrfToken,
    };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;
    if (options.ifMatch !== undefined) headers["If-Match"] = String(options.ifMatch);
    const response = await this.fetchImplementation(`${this.baseUrl}${path}`, {
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      cache: "no-store",
      credentials: "same-origin",
      headers,
      method,
      ...(options.signal ? { signal: options.signal } : {}),
    });
    await this.assertOk(response);
    return response;
  }

  private async mutateJson<T>(
    method: "PUT" | "PATCH" | "DELETE",
    path: string,
    body: unknown,
    options: MutationOptions,
  ): Promise<T> {
    const response = await this.mutate(method, path, body, options);
    if (response.status === 204 || response.headers.get("content-length") === "0") {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  private async assertOk(response: Response): Promise<void> {
    if (response.ok) return;
    let problem: ApiProblem | null = null;
    if (response.headers.get("content-type")?.includes("json")) {
      try { problem = await response.json() as ApiProblem; } catch { problem = null; }
    }
    throw new ApiRequestError(response.status, problem);
  }
}
